import type { ReactNode } from 'react';
import { Button } from '@/ui';

interface StepLayoutProps {
  title: string;
  subtitle?: string;
  children: ReactNode;
  /** Primary action label (e.g. "Save & continue", "Finish"). */
  primaryLabel: string;
  onPrimary: () => void;
  primaryLoading?: boolean;
  primaryDisabled?: boolean;
  /** Secondary action — usually "Skip for now". Hidden when omitted. */
  secondaryLabel?: string;
  onSecondary?: () => void;
  /** Inline error banner shown above the action row. */
  error?: string | null;
  /** Inline success/info banner. Lower contrast than error. */
  notice?: string | null;
}

// Common scaffold for every step body. Header → form → action row.
// Keeps step components focused on their fields, not layout chrome.
export function StepLayout({
  title,
  subtitle,
  children,
  primaryLabel,
  onPrimary,
  primaryLoading,
  primaryDisabled,
  secondaryLabel,
  onSecondary,
  error,
  notice,
}: StepLayoutProps) {
  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-1.5">
        <h1 className="text-heading-lg text-ink">{title}</h1>
        {subtitle && <p className="text-body-md text-ink-subtle">{subtitle}</p>}
      </header>

      <div>{children}</div>

      {notice && (
        <div className="rounded-lg border border-border bg-surface-2 px-3 py-2.5 text-body-sm text-ink">
          {notice}
        </div>
      )}
      {error && (
        <div
          role="alert"
          className="rounded-lg border border-sev-critical bg-sev-critical-soft px-3 py-2.5 text-body-sm text-sev-critical-ink"
        >
          {error}
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-2 border-t border-border">
        {secondaryLabel && onSecondary ? (
          <Button variant="ghost" onClick={onSecondary} disabled={primaryLoading}>
            {secondaryLabel}
          </Button>
        ) : (
          <span />
        )}
        <Button
          variant="primary"
          onClick={onPrimary}
          loading={primaryLoading}
          disabled={primaryDisabled}
        >
          {primaryLabel}
        </Button>
      </div>
    </div>
  );
}
